import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Button from './Button';
import Input from './Input';
import Loader from './Loader';
import ResultFlowGraph, { typeLabel } from './ResultFlowGraph';

/*
 * Survey graph paths
 * ------------------
 * Draws the whole survey as a graph: every question, every group, and every
 * route the flow can take (default order, choice and branch jumps, group jumps
 * and the Success/Failure ends). It renders with the same ResultFlowGraph as
 * the Result answers visualiser, in its structure-only mode — there is no
 * result here, so no path is highlighted.
 */

const GET_SURVEY_GRAPH_PATHS = gql`
  query getSurveyGraphPaths($surveyId: String!) {
    getSurveyGraphPaths(surveyId: $surveyId) {
      survey {
        id
        name
        startQuestionId
        questionCount
        groupCount
        sequentialEdges
        jumpEdges
        endEdges
        canFail
        pathCount
        pathCountCapped
        shortestPath
        longestPath
        unreachable {
          questionId
          type
          questionText
        }
        deadEnds {
          questionId
          type
          questionText
        }
        outsideFlowCount
        outsideFlow {
          questionId
          type
          questionText
        }
      }
      graphEdges {
        from
        to
        kind
        choiceLabel
        taken
      }
      groups {
        id
        label
        parentId
      }
      items {
        questionId
        type
        questionText
        groupId
        groupLabel
        inStructure
        visited
        order
        options {
          index
          text
          kind
        }
        sliderMin
        sliderMax
      }
    }
  }
`;

function QuestionList({ title, hint, questions }) {
  return <div style={styles.listBlock}>
    <h3 style={styles.sectionTitle}>
      {title} — {questions.length}
    </h3>
    <p style={styles.listHint}>{hint}</p>
    <ul style={styles.list}>
      {questions.map(question => (
        <li key={question.questionId} style={styles.listItem}>
          <span style={styles.listType}>{typeLabel(question.type)}</span>
          <span>{question.questionText || '(no question text)'}</span>
          <span style={styles.listId}>{question.questionId}</span>
        </li>
      ))}
    </ul>
  </div>
}

function SurveyGraphPaths() {
  const [surveyId, setSurveyId] = useState('');

  const [fetchGraph, { loading, error, data }] = useLazyQuery(
    GET_SURVEY_GRAPH_PATHS,
    { fetchPolicy: 'network-only' }
  );

  const handleLoad = () => {
    if (surveyId.trim()) {
      fetchGraph({ variables: { surveyId: surveyId.trim() } });
    }
  };

  const payload = data && data.getSurveyGraphPaths;
  const meta = payload && payload.survey;

  return (
    <div style={styles.container}>
      <Link to="/development-dashboard" style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>Survey graph paths</h1>
      <p>
        Enter a survey id to see the survey as a graph, with every question
        connected by the paths the flow can take.
      </p>

      <div style={styles.form}>
        <Input
          type="text"
          value={surveyId}
          onChange={e => setSurveyId(e.target.value)}
          placeholder="Enter survey id"
          style={styles.input}
        />
        <Button onClick={handleLoad} primary style={styles.loadButton}>
          Load survey
        </Button>
      </div>

      {loading && <Loader isProcessing />}
      {error && <p style={styles.errorText}>Error: {error.message}</p>}

      {meta && (
        <div style={styles.meta}>
          <h2 style={styles.surveyName}>{meta.name || 'Unknown survey'}</h2>
          <div style={styles.metaGrid}>
            <span>
              <strong>Survey:</strong> {meta.id}
            </span>
            <span>
              <strong>Questions:</strong> {meta.questionCount}
            </span>
            <span>
              <strong>Groups:</strong> {meta.groupCount}
            </span>
            <span>
              <strong>Possible paths:</strong> {meta.pathCount}
              {meta.pathCountCapped ? '+' : ''}
            </span>
            <span>
              <strong>Questions per path:</strong>{' '}
              {meta.shortestPath != null
                ? `${meta.shortestPath} - ${meta.longestPath}`
                : '-'}
            </span>
            <span>
              <strong>Connections:</strong> {meta.sequentialEdges} in order,{' '}
              {meta.jumpEdges} jumps, {meta.endEdges} ends
            </span>
            <span>
              <strong>Can screen out:</strong> {meta.canFail ? 'yes' : 'no'}
            </span>
            {meta.outsideFlowCount > 0 && (
              <span>
                <strong>Not in the flow:</strong> {meta.outsideFlowCount}
              </span>
            )}
          </div>
          {meta.pathCountCapped && (
            <p style={styles.note}>
              The survey has more routes than this tool walks, so the number of
              paths is a lower bound. Routes that loop back to a question they
              already visited are not counted twice.
            </p>
          )}
        </div>
      )}

      {payload && payload.items && payload.items.length > 0 && (
        <div style={styles.graphContainer}>
          <h3 style={styles.sectionTitle}>Flow graph</h3>
          <ResultFlowGraph
            items={payload.items}
            graphEdges={payload.graphEdges}
            groups={payload.groups}
            structureOnly
          />
        </div>
      )}

      {meta && meta.unreachable.length > 0 && (
        <QuestionList
          title="Questions no path reaches"
          hint="No connection leads to these questions, so a user never sees them."
          questions={meta.unreachable}
        />
      )}

      {meta && meta.deadEnds.length > 0 && (
        <QuestionList
          title="Questions without a way out"
          hint="These questions have no connection onwards and do not end the survey."
          questions={meta.deadEnds}
        />
      )}

      {meta && meta.outsideFlow.length > 0 && (
        <QuestionList
          title="Questions that are not in the survey flow"
          hint="These questions belong to the survey but the flow does not contain them, so they are left out of the graph."
          questions={meta.outsideFlow}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600
  },
  form: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    maxWidth: '620px',
    marginBottom: '16px'
  },
  input: {
    flex: 1
  },
  loadButton: {
    minWidth: '140px',
    height: '38px'
  },
  errorText: {
    color: '#c0392b'
  },
  meta: {
    padding: '14px 16px',
    marginBottom: '20px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#fafafa'
  },
  surveyName: {
    margin: '0 0 10px 0',
    fontSize: '18px'
  },
  metaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 24px',
    fontSize: '13px',
    color: '#444'
  },
  note: {
    margin: '10px 0 0 0',
    fontSize: '12px',
    color: '#777'
  },
  graphContainer: {
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '15px',
    margin: '0 0 8px 0'
  },
  listBlock: {
    marginBottom: '24px'
  },
  listHint: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    color: '#777'
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    fontSize: '13px'
  },
  listType: {
    minWidth: '130px',
    fontWeight: 600,
    color: '#1f6fb2'
  },
  listId: {
    marginLeft: 'auto',
    color: '#999',
    fontSize: '12px'
  }
};

export default SurveyGraphPaths;
